'use client';

import React, { useState } from 'react';
import { Clock, Check } from 'lucide-react';

type Frequency = '15min' | '1hr' | '6hr' | '24hr' | 'manual';

interface FrequencyOption {
  value: Frequency;
  label: string;
  description: string;
}

const FREQUENCY_OPTIONS: FrequencyOption[] = [
  { value: '15min', label: 'Every 15 min', description: 'Near real-time — higher API usage' },
  { value: '1hr', label: 'Every hour', description: 'Balanced refresh rate' },
  { value: '6hr', label: 'Every 6 hours', description: 'Recommended for most athletes' },
  { value: '24hr', label: 'Once a day', description: 'Low API usage, daily digest' },
  { value: 'manual', label: 'Manual only', description: 'Sync only when you press "Sync Now"' },
];

export default function SyncSettingsCard() {
  const [selected, setSelected] = useState<Frequency>('6hr');
  const [saved, setSaved] = useState(false);

  function handleSave() {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  return (
    <div className="rounded-2xl border border-[hsl(var(--border))] bg-white overflow-hidden">
      {/* Card header */}
      <div className="px-5 py-4 border-b border-[hsl(var(--border))] flex items-center gap-2.5">
        <div className="w-8 h-8 rounded-lg bg-[hsl(var(--primary)/0.08)] border border-[hsl(var(--primary)/0.15)] flex items-center justify-center shrink-0">
          <Clock size={14} className="text-[hsl(var(--primary))]" />
        </div>
        <div>
          <p className="text-sm font-semibold text-[hsl(var(--foreground))]">Data Refresh Frequency</p>
          <p className="text-[11px] text-[hsl(var(--muted-foreground))]">Control how often KOM Gatherers pulls your latest Strava data</p>
        </div>
      </div>

      {/* Options */}
      <div className="px-5 py-4 space-y-2">
        {FREQUENCY_OPTIONS.map((opt) => {
          const isActive = selected === opt.value;
          return (
            <button
              key={opt.value}
              onClick={() => setSelected(opt.value)}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border text-left transition-all duration-150 ${
                isActive
                  ? 'border-[hsl(var(--primary)/0.4)] bg-[hsl(var(--primary)/0.05)]'
                  : 'border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]'
              }`}
            >
              <div>
                <p className={`text-sm font-semibold ${isActive ? 'text-[hsl(var(--primary))]' : 'text-[hsl(var(--foreground))]'}`}>
                  {opt.label}
                </p>
                <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5">{opt.description}</p>
              </div>
              {isActive && (
                <div className="w-5 h-5 rounded-full bg-[hsl(var(--primary))] flex items-center justify-center shrink-0 ml-3">
                  <Check size={11} className="text-white" strokeWidth={3} />
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Save */}
      <div className="px-5 py-4 border-t border-[hsl(var(--border))] flex items-center justify-between">
        <p className="text-[11px] text-[hsl(var(--muted-foreground))]">
          Changes take effect on the next scheduled sync cycle.
        </p>
        <button
          onClick={handleSave}
          className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all duration-200 ${
            saved
              ? 'bg-emerald-500 text-white' :'bg-[hsl(var(--primary))] text-white hover:opacity-90'
          }`}
        >
          {saved ? (
            <>
              <Check size={12} strokeWidth={3} />
              Saved
            </>
          ) : (
            'Save Settings'
          )}
        </button>
      </div>
    </div>
  );
}
